import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Sparkles,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  User,
  TrendingUp,
  Info,
  ThumbsUp,
  ArrowRight,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";
import {
  Alert,
  AlertDescription,
} from "./ui/alert";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ShortlistRecommendation({ jobId, candidates, job, open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [shortlist, setShortlist] = useState(null);
  const [generated, setGenerated] = useState(false);

  const generateShortlist = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/jobs/${jobId}/generate-shortlist`);
      setShortlist(response.data);
      setGenerated(true);
    } catch (error) {
      console.error("Error generating shortlist:", error);
      toast.error("Error al generar la shortlist");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    onClose();
    // Reset state when closing
    setTimeout(() => {
      setShortlist(null);
      setGenerated(false);
    }, 300);
  };

  const getScoreColor = (score) => {
    if (score >= 75) return "text-emerald-600";
    if (score >= 50) return "text-amber-600";
    return "text-red-600";
  };

  const getRiskLevel = (risks) => {
    if (!risks || risks.length === 0) return { level: "low", color: "text-emerald-600", bg: "bg-emerald-50" };
    if (risks.length === 1) return { level: "medium", color: "text-amber-600", bg: "bg-amber-50" };
    return { level: "high", color: "text-red-600", bg: "bg-red-50" };
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-xl font-heading flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Shortlist Recomendada
          </DialogTitle>
          <DialogDescription>
            {job?.title} • {candidates.length} candidatos analizados
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-auto py-4">
          {!generated ? (
            // Initial state - prompt to generate
            <div className="text-center py-8 space-y-6">
              <div className="w-20 h-20 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto">
                <Sparkles className="w-10 h-10 text-primary" />
              </div>
              
              <div className="space-y-2 max-w-md mx-auto">
                <h3 className="font-heading font-semibold text-lg">
                  Generar Recomendación Inteligente
                </h3>
                <p className="text-sm text-muted-foreground">
                  La IA analizará todos los candidatos y te recomendará los <strong>Top 3-5</strong> más 
                  adecuados para avanzar a entrevistas, con justificación y alertas.
                </p>
              </div>

              <Alert className="max-w-md mx-auto bg-blue-50 border-blue-200">
                <Info className="h-4 w-4 text-blue-600" />
                <AlertDescription className="text-blue-700 text-sm">
                  Esta es una <strong>sugerencia</strong>, no una decisión automática. 
                  Vos tenés la última palabra.
                </AlertDescription>
              </Alert>

              <Button 
                size="lg" 
                onClick={generateShortlist}
                disabled={loading || candidates.length < 2}
                className="gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Analizando candidatos...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4" />
                    Generar Shortlist
                  </>
                )}
              </Button>

              {candidates.length < 2 && (
                <p className="text-sm text-muted-foreground">
                  Necesitás al menos 2 candidatos para generar una shortlist
                </p>
              )}
            </div>
          ) : (
            // Results
            <div className="space-y-6">
              {/* Summary */}
              <div className="p-4 rounded-lg bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20">
                <div className="flex items-start gap-3">
                  <ThumbsUp className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-sm">
                      {shortlist?.summary || `Recomendamos ${shortlist?.recommendations?.length || 0} candidatos para avanzar`}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Basado en compatibilidad, experiencia y requisitos del puesto
                    </p>
                  </div>
                </div>
              </div>

              {/* Recommendations */}
              <div className="space-y-3">
                {shortlist?.recommendations?.map((rec, index) => {
                  const riskInfo = getRiskLevel(rec.risks);
                  
                  return (
                    <Card key={rec.candidate_id} className="overflow-hidden">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          {/* Rank */}
                          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center shrink-0">
                            <span className="text-white font-bold">#{index + 1}</span>
                          </div>

                          {/* Main Info */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-heading font-semibold">
                                {rec.name}
                              </h4>
                              <Badge className={getScoreColor(rec.score)}>
                                {rec.score}% compatible
                              </Badge>
                              {rec.risks?.length > 0 && (
                                <Badge variant="outline" className={riskInfo.color}>
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  {rec.risks.length} alerta{rec.risks.length > 1 ? 's' : ''}
                                </Badge>
                              )}
                            </div>

                            {/* Justification */}
                            <p className="text-sm text-muted-foreground mt-2">
                              {rec.justification}
                            </p>

                            {/* Strengths */}
                            {rec.strengths?.length > 0 && (
                              <div className="mt-3 flex flex-wrap gap-1">
                                {rec.strengths.slice(0, 3).map((s, i) => (
                                  <Badge key={i} variant="secondary" className="text-xs bg-emerald-50 text-emerald-700">
                                    <CheckCircle2 className="w-3 h-3 mr-1" />
                                    {s}
                                  </Badge>
                                ))}
                              </div>
                            )}

                            {/* Risks */}
                            {rec.risks?.length > 0 && (
                              <div className="mt-2 p-2 rounded bg-amber-50 border border-amber-200">
                                <p className="text-xs font-medium text-amber-800 mb-1">Alertas a considerar:</p>
                                <ul className="text-xs text-amber-700 space-y-0.5">
                                  {rec.risks.map((r, i) => (
                                    <li key={i} className="flex items-start gap-1">
                                      <AlertTriangle className="w-3 h-3 shrink-0 mt-0.5" />
                                      {r}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>

                          {/* Action */}
                          <Link to={`/candidates/${rec.candidate_id}`}>
                            <Button variant="ghost" size="sm" className="shrink-0">
                              Ver perfil
                              <ArrowRight className="w-4 h-4 ml-1" />
                            </Button>
                          </Link>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Not Recommended */}
              {shortlist?.not_recommended?.length > 0 && (
                <div className="pt-4 border-t">
                  <p className="text-sm font-medium text-muted-foreground mb-2">
                    No recomendados para avanzar ({shortlist.not_recommended.length})
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {shortlist.not_recommended.map((nr, i) => (
                      <Badge key={i} variant="outline" className="text-muted-foreground">
                        {nr.name} ({nr.score}%) - {nr.reason}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Disclaimer */}
              <Alert className="bg-muted/50">
                <Info className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  Esta recomendación es generada por IA como apoyo a tu decisión. 
                  Te sugerimos validar con entrevistas y referencias.
                </AlertDescription>
              </Alert>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="pt-4 border-t flex justify-end gap-2">
          {generated && (
            <Button variant="outline" onClick={() => { setShortlist(null); setGenerated(false); }}>
              Regenerar
            </Button>
          )}
          <Button onClick={handleClose}>
            {generated ? "Cerrar" : "Cancelar"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
